import type { ApiResponse } from "apisauce"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useLatestRequestId } from "../../../../../hooks/use-latest-request-id.ts"
import {
  approveEdition,
  getLeadEducation,
  publishEdition,
  requestMore,
  retryEdition,
  startGeneration
} from "../api/education.services.ts"
import type {
  EditionView,
  EducationApiError,
  InFlightPackageView,
  LeadEducationView,
  PackageView,
  RequestMoreData,
  StartGenerationData
} from "../types/education.types.ts"

export type LeadEducationResult =
  | { ok: true }
  | { ok: false; error: EducationApiError }

// `askMore` needs the created edition back (its id, to select it) — the
// other actions here only ever need to know whether they succeeded, since
// `refresh()` already re-fetches the full view.
export type AskMoreResult =
  | { ok: true; edition: EditionView | null }
  | { ok: false; error: EducationApiError }

interface UseLeadEducationResult {
  data: LeadEducationView | null
  loading: boolean
  error: EducationApiError | null
  refresh: () => Promise<void>
  generate: (body: StartGenerationData) => Promise<LeadEducationResult>
  askMore: (
    trackId: string,
    body: RequestMoreData,
    idempotencyKey: string
  ) => Promise<AskMoreResult>
  retry: (editionId: string) => Promise<LeadEducationResult>
  publish: (editionId: string) => Promise<LeadEducationResult>
  approve: (editionId: string) => Promise<LeadEducationResult>
}

const POLL_INTERVAL_MS = 3_000
const MAX_POLL_INTERVAL_MS = 30_000

const hasPendingEdition = (view: LeadEducationView | null): boolean => {
  if (!view) return false
  const packages: (PackageView | InFlightPackageView)[] = [
    view.package,
    view.in_flight_package
  ].filter((pkg): pkg is PackageView | InFlightPackageView => !!pkg)

  return packages.some((pkg) =>
    pkg.tracks.some((track) =>
      track.editions.some((edition) => edition.status === "pending")
    )
  )
}

/**
 * Loads a lead's education state, polls `getLeadEducation` every 3s while
 * anything in `package`/`in_flight_package` is still `pending`, and exposes
 * the write actions a task can trigger on it. Every action refreshes on
 * success rather than hand-merging the response, so a poll that would
 * otherwise start (an edition just went `pending`) starts from the same
 * single code path as the initial load.
 */
export const useLeadEducation = (leadId: string): UseLeadEducationResult => {
  const { t } = useTranslation()
  const [data, setData] = useState<LeadEducationView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<EducationApiError | null>(null)

  const { claim, isCurrent, invalidate } = useLatestRequestId()
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const generateKeyRef = useRef(crypto.randomUUID())
  const generateInFlightRef = useRef<Promise<LeadEducationResult> | null>(null)
  // Whether the last *successful* fetch still had something pending — a
  // failed poll tick keeps retrying while this is true (a network blip
  // during generation must not strand the UI on "Generating…" forever), but
  // an unrelated first-load failure (nothing yet known to be pending) does
  // not turn into an infinite retry loop.
  const pendingRef = useRef(false)
  const pollBackoffRef = useRef(POLL_INTERVAL_MS)

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const networkErrorFallback = useCallback(
    (): EducationApiError => ({
      code: "network_error",
      message: t("projectEducation.error.network"),
      request_id: "",
      retryable: true,
      field_errors: {}
    }),
    [t]
  )

  // `silent` skips the loading spinner for a poll tick or a post-action
  // refresh — only the lead-changing/initial fetch shows it.
  const fetchOnce = useCallback(
    async (targetLeadId: string, silent: boolean) => {
      clearPollTimer()
      const requestId = claim()
      if (!silent) setLoading(true)

      const response = await getLeadEducation(targetLeadId)
      if (!isCurrent(requestId)) return

      if (response.ok) {
        if (response.data) {
          setData(response.data)
          setError(null)
          pendingRef.current = hasPendingEdition(response.data)
          if (pendingRef.current) {
            pollBackoffRef.current = POLL_INTERVAL_MS
            pollTimerRef.current = setTimeout(() => {
              void fetchOnce(targetLeadId, true)
            }, pollBackoffRef.current)
          }
        }
      } else {
        setError(response.data ?? null)
        if (pendingRef.current) {
          pollTimerRef.current = setTimeout(() => {
            void fetchOnce(targetLeadId, true)
          }, pollBackoffRef.current)
          pollBackoffRef.current = Math.min(
            pollBackoffRef.current * 2,
            MAX_POLL_INTERVAL_MS
          )
        }
      }
      setLoading(false)
    },
    [claim, isCurrent, clearPollTimer]
  )

  useEffect(() => {
    invalidate()
    clearPollTimer()
    setData(null)
    setError(null)
    pendingRef.current = false
    pollBackoffRef.current = POLL_INTERVAL_MS
    if (!leadId) {
      setLoading(false)
      return
    }
    void fetchOnce(leadId, false)

    return () => {
      invalidate()
      clearPollTimer()
    }
    // `fetchOnce` closes over stable refs/callbacks only (see its own deps);
    // this effect is meant to re-run on `leadId` alone.
  }, [leadId])

  const refresh = useCallback(async () => {
    if (!leadId) return
    await fetchOnce(leadId, true)
  }, [leadId, fetchOnce])

  const settle = useCallback(
    async (
      call: Promise<ApiResponse<unknown, EducationApiError>>
    ): Promise<LeadEducationResult> => {
      const response = await call
      if (response.ok) {
        await refresh()
        return { ok: true }
      }
      return { ok: false, error: response.data ?? networkErrorFallback() }
    },
    [refresh, networkErrorFallback]
  )

  const generate = useCallback(
    (body: StartGenerationData): Promise<LeadEducationResult> => {
      if (generateInFlightRef.current) return generateInFlightRef.current

      const key = generateKeyRef.current
      const attempt = (async (): Promise<LeadEducationResult> => {
        const response = await startGeneration(leadId, body, key)
        // A request that never got an answer keeps its key, so retrying it
        // replays the same attempt; any answer ends the attempt.
        if (response.status) generateKeyRef.current = crypto.randomUUID()
        if (response.ok) {
          await refresh()
          return { ok: true }
        }
        return { ok: false, error: response.data ?? networkErrorFallback() }
      })().finally(() => {
        generateInFlightRef.current = null
      })
      generateInFlightRef.current = attempt
      return attempt
    },
    [leadId, refresh, networkErrorFallback]
  )

  const askMore = useCallback(
    async (
      trackId: string,
      body: RequestMoreData,
      idempotencyKey: string
    ): Promise<AskMoreResult> => {
      const response = await requestMore(trackId, body, idempotencyKey)
      if (response.ok) {
        await refresh()
        return { ok: true, edition: response.data?.details.data ?? null }
      }
      return { ok: false, error: response.data ?? networkErrorFallback() }
    },
    [refresh, networkErrorFallback]
  )

  const retry = useCallback(
    (editionId: string) => settle(retryEdition(editionId)),
    [settle]
  )
  const publish = useCallback(
    (editionId: string) => settle(publishEdition(editionId)),
    [settle]
  )
  const approve = useCallback(
    (editionId: string) => settle(approveEdition(editionId)),
    [settle]
  )

  return {
    data,
    loading,
    error,
    refresh,
    generate,
    askMore,
    retry,
    publish,
    approve
  }
}
